<template>
  <Teleport to="body">
    <div class="select" ref="selectHTMLElement">
      <span
        class="select-span"
        v-for="(value, index) in hoverTextArray"
        :key="index"
        :class="{
          'select-span-fn': operate.fnCheck.value && value.length > 0,
          'select-span-no-fn': !operate.fnCheck.value && value.length > 0,
          single_span: hoverTextArray.length == 1,
          'select-span-no-data': value.length == 0,
          'small-font': value.length >= 6,
        }"
        >{{ value }}</span
      >
    </div>
    <div class="line" v-show="hoverTextArray.length > 0" ref="lineHTMLElement"></div>
  </Teleport>
</template>

<script lang="ts">
const hoverKK = ref<KK | null>(null)
export { hoverKK }
</script>

<script setup lang="ts">
import { KK } from '@/define/define'
import { kbVKTexture, mediaVKS, mouseVKs } from '@/define/vkcode'
import { setData } from '@/service/driver/data'
import { operate } from '@/service/driver/operate'
import { computed, nextTick, onMounted, PropType, Teleport, ref, watch } from 'vue'
import i18n from '@/languages/i18n'
import { MagnetSeniorType } from '@libs/types/enum'
import { delay } from '@libs/utils/tools'
import { getInternalActionKeyDef, InternalActionKeyId } from '@/define/internal_action_keys'
import { configSet } from '@/service/driver/config_set'
import { VK } from '@/define/define'
const { t } = i18n.global
const selectHTMLElement = ref<HTMLElement | null>(null)
const lineHTMLElement = ref<HTMLElement | null>(null)

const onboardSwitchActionIds = [InternalActionKeyId.deviceSwitchToProfile1, InternalActionKeyId.deviceSwitchToProfile2, InternalActionKeyId.deviceSwitchToProfile3, InternalActionKeyId.deviceSwitchToProfile4] as VK[]

const getInternalActionLabel = (id: VK): string => {
  const slotIndex = onboardSwitchActionIds.indexOf(id)
  if (slotIndex >= 0 && configSet.isOnboardConfigSupported) {
    const slot = configSet.onboardConfigList.value.find((s) => s.Index === slotIndex)
    const localIdx = slot?.LocalConfigIndex ?? null
    const configName = localIdx !== null ? configSet.configList.value[localIdx]?.Name || '' : t('keyboard_preset_slot_empty')
    return `${t('config')} ${slotIndex + 1}: ${configName}`
  }
  const def = getInternalActionKeyDef(id)
  return def ? t(def.labelKey) : ''
}

const getActionData = (kk: KK): string[] => {
  const { KeyMap, Macros, Magenet, KeyMapFire, KeyMapModifier } = setData.getActionData

  const vk = KeyMap[kk]
  if (vk) {
    if (kbVKTexture[vk]) {
      return [kbVKTexture[vk]]
    } else if (mouseVKs[vk]) {
      return [t(mouseVKs[vk])]
    } else if (mediaVKS[vk]) {
      return [t(mediaVKS[vk])]
    }

    const internalAction = getInternalActionKeyDef(vk)
    if (internalAction) {
      return [getInternalActionLabel(vk)]
    }
  }

  if (Macros[kk]) {
    return [t('macro_name')]
  }
  if (KeyMapFire && KeyMapFire[kk]) {
    return [t('fire_key')]
  }
  if (KeyMapModifier && KeyMapModifier[kk] && KeyMapModifier[kk].length > 0) {
    return [t('action_set_combination_key')]
  }

  const magnet = Magenet[kk]
  if (magnet) {
    const keys = magnet.Keys
    if (magnet.KeyType == MagnetSeniorType.SeniorKeyTypeTGL || magnet.KeyType == MagnetSeniorType.SeniorKeyTypeKME || magnet.KeyType == MagnetSeniorType.SeniorKeyTypeRS) {
      keys.length = 1
    } else if (magnet.KeyType == MagnetSeniorType.SeniorKeyTypeMT) {
      keys.length = 2
    }

    let values: string[] = []
    keys.forEach((e) => {
      let code = e.KeyCode.filter((e) => e > 0)
      if (code.length > 1) {
        values.push(t('action_set_combination_key'))
      } else if (code.length == 1) {
        if (kbVKTexture[code[0]]) {
          values.push(kbVKTexture[code[0]])
        } else if (mouseVKs[code[0]]) {
          values.push(t(mouseVKs[code[0]]))
        } else if (mediaVKS[code[0]]) {
          values.push(t(mediaVKS[code[0]]))
        } else {
          const internalAction = getInternalActionKeyDef(code[0])
          values.push(internalAction ? getInternalActionLabel(code[0]) : '')
        }
      } else {
        values.push('')
      }
    })
    return values
  }
  return []
}

// getActionData 已在上方定义，computed 可安全引用
const hoverTextArray = computed<string[]>(() => {
  if (hoverKK.value == null) return []
  const values = getActionData(hoverKK.value)
  return values.filter((v) => v && v.trim().length > 0)
})

// 当 hover 内容变化时，重新计算浮框位置（加 selectHTMLElement 守卫防止组件卸载后操作 DOM）
watch(hoverTextArray, async (newVals) => {
  if (newVals.length > 0) {
    await nextTick()
    if (selectHTMLElement.value) {
      changeHoverRect()
    }
  }
})

onMounted(() => {
  updateHover()
})

const props = defineProps({
  currentSelectElement: {
    type: HTMLElement as PropType<HTMLElement>,
    required: false,
  },
})

// 由父组件调用，触发悬浮框位置重算（数据由 computed hoverTextArray 自动更新，无需手动刷新）
const updateHover = () => {
  if (!selectHTMLElement.value) return
  if (!lineHTMLElement.value) return
  if (hoverTextArray.value.length > 0) {
    changeHoverRect()
  }
}
// 将方法或者属性暴露给父组件
defineExpose({
  updateHover,
})

const getDeviceElement = () => document.getElementById('virtual_device') as HTMLElement
const selectHeight = 42

const changeHoverRect = async () => {
  if (!props.currentSelectElement) return
  const dv = getDeviceElement()
  if (!dv) return

  const target = props.currentSelectElement
  const hover = selectHTMLElement.value
  const line = lineHTMLElement.value

  if (!hover || !line) return

  hover.style.opacity = '0'
  line.style.opacity = '0'
  await delay(3)

  const left_margin = hoverTextArray.value.length == 1 ? 78 : 18
  const right_margin = left_margin
  const dvRect = dv.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  const anchorY = targetRect.top + targetRect.height / 2
  const hoverTop = anchorY - selectHeight / 2
  const hoverWidth = hover.offsetWidth
  const targetCenterX = targetRect.left + targetRect.width / 2
  const isRight = targetCenterX > dvRect.left + dvRect.width / 2

  // 获取中间面板边界，防止悬浮标签和连线溢出到侧栏区域
  const centerShell = dv.closest('.layout_center_shell') || dv.closest('.center') || dv.closest('.main')
  const maxRight = centerShell ? centerShell.getBoundingClientRect().right : window.innerWidth
  const minLeft = centerShell ? centerShell.getBoundingClientRect().left : 0

  let hoverLeft = isRight ? dvRect.right + right_margin : dvRect.left - left_margin - hoverWidth

  // 边界约束：不让悬浮标签超出中间面板区域
  if (hoverLeft + hoverWidth > maxRight) {
    hoverLeft = maxRight - hoverWidth - 4
  }
  if (hoverLeft < minLeft) {
    hoverLeft = minLeft + 4
  }

  const lineStart = isRight ? targetRect.right : hoverLeft + hoverWidth
  const lineEnd = isRight ? hoverLeft : targetRect.left
  const clampedLineLeft = Math.max(Math.min(lineStart, lineEnd), minLeft)
  const clampedLineRight = Math.min(Math.max(lineStart, lineEnd), maxRight)

  line.style.top = anchorY + 'px'
  line.style.left = clampedLineLeft + 'px'
  line.style.width = Math.max(clampedLineRight - clampedLineLeft, 0) + 'px'

  hover.style.top = hoverTop + 'px'
  hover.style.left = hoverLeft + 'px'
  hover.style.height = selectHeight + 'px'
  hover.style.opacity = '1'
  line.style.opacity = '1'
}
</script>

<style lang="less">
.select {
  position: fixed;
  z-index: 1200;
  width: intrinsic;
  width: -moz-max-content;
  width: -webkit-max-content;
  top: 0;
  color: var(--action-btn-ghost-text);
  text-align: center;
  display: flex;
  justify-content: space-between;
  min-width: 42px;
  pointer-events: none;

  .select-span {
    /* 使用幽灵毛玻璃标准 - V2 视觉规范 */
    background: var(--glass-ghost-bg);
    border: 1px solid var(--glass-ghost-border);
    backdrop-filter: var(--glass-ghost-blur);
    border-radius: var(--radius-s);
    color: var(--action-btn-ghost-text);
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-width: 42px;
    max-width: 50px;
    word-wrap: break-word;
    font-weight: bold;
    padding: 0 8px; /* 增加内边距 */
    font-size: 12px;
    margin-left: 2px;
    box-shadow: none; /* 幽灵层严禁使用投影 */
    transition: var(--transition-base);

    // &.select-span-fn {
    //   /* Fn 层重新设计：采用通透的蓝色幽灵层 */
    //   background: rgba(81, 162, 255, 0.25);
    //   border-color: rgba(81, 162, 255, 0.5);
    // }

    // &.select-span-no-fn {
    //   /* 常规层采用标准幽灵层 */
    //   background: var(--glass-ghost-bg);
    // }

    /* 无数据状态：采用极淡的磨砂感 */
    &.select-span-no-data {
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
      backdrop-filter: blur(5px);
    }

    &.single_span {
      max-width: 120px !important; /* 扩大单文本宽度上限 */
    }

    &.small-font {
      font-size: 10px !important; /* 当文字过长时缩小字号 */
    }
  }

  .select-span:nth-child(1) {
    margin-left: 0px;
  }
}

.line {
  height: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.72);
  width: 120px;
  position: fixed;
  top: 0;
  left: 0;
  opacity: 0.8;
  pointer-events: none;
  z-index: 1199;
}
</style>
